import React, { useRef } from 'react';

import { buildParentOption } from 'bundles/crm/parent_helpers';
import { get } from 'helpers/api';
import { Select } from 'components/ui/select';

import type { ParentOption } from 'bundles/crm/types';
import type { SelectableParentType } from 'bundles/apex/tasks/types';

interface Props {
  disabled?: boolean;
  parentType: SelectableParentType;
  value: ParentOption | null;
  onChange: (option: ParentOption | null) => void;
}

const MIN_QUERY_LENGTH = 2;

interface Searchable {
  id: number;
  displayName?: string;
  name?: string;
}

type Adapter = (query: string) => Promise<ParentOption[]>;

function toOptions(records: Searchable[], parentType: SelectableParentType): ParentOption[] {
  return records
    .filter(record => record.displayName || record.name)
    .map(record => buildParentOption(
      { id: record.id, type: parentType },
      parentType,
      (record.displayName || record.name) as string
    ));
}

const adapters: Record<SelectableParentType, Adapter> = {
  'Apex::Deal': async (query) => {
    const deals: Searchable[] = await get('/deals/search', { params: { query } });
    return toOptions(deals, 'Apex::Deal');
  },
  'Backoffice::Contact': async (query) => {
    const contacts: Searchable[] = await get('/backoffice/contacts/search', { params: { query } });
    return toOptions(contacts, 'Backoffice::Contact');
  },
};

export default function ParentSearch({ disabled, parentType, value, onChange }: Props) {
  const requestId = useRef(0);

  const loadOptions = (inputValue: string, callback: (options: ParentOption[]) => void) => {
    const trimmed = inputValue.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      callback([]);
      return;
    }

    const currentId = ++requestId.current;
    adapters[parentType](trimmed)
      .then((options) => {
        if (currentId === requestId.current) callback(options);
      })
      .catch(() => {
        if (currentId === requestId.current) callback([]);
      });
  };

  return (
    <Select<ParentOption>
      isClearable
      isDisabled={disabled}
      loadOptions={loadOptions}
      noOptionsMessage={({ inputValue }) => (
        inputValue.length < MIN_QUERY_LENGTH ? 'Type at least 2 characters to search' : 'No results'
      )}
      placeholder="Search…"
      value={value}
      onChange={onChange}
    />
  );
}
